import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  BookOpenIcon,
  DocumentTextIcon,
  CloudArrowUpIcon,
  AcademicCapIcon,
  PlayIcon,
  DocumentIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';

const CourseManagement = () => {
  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const [showAddModule, setShowAddModule] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // State for data
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [newCourse, setNewCourse] = useState({
    name: '',
    description: '',
    write_up: '',
    duration_months: '',
    cost_amount: '',
    cost_currency: 'NGN',
    is_active: true,
    pdf_file: null
  });

  const [newModule, setNewModule] = useState({
    name: '',
    description: '',
    course: '',
    duration: '',
    learning_objectives: ''
  });

  useEffect(() => {
    setIsLoaded(true);
    loadCourseData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCourseData = async () => {
    try {
      setLoading(true);
      const [coursesData, materialsData] = await Promise.all([
        apiService.getCourses().catch(err => { console.error('Failed to load courses:', err); return []; }),
        apiService.getMaterials().catch(err => { console.error('Failed to load materials:', err); return []; })
      ]);

      setCourses(Array.isArray(coursesData) ? coursesData : []);
      setMaterials(Array.isArray(materialsData) ? materialsData : []);
      
      // Load modules for the first course if available
      if (coursesData.length > 0) {
        setSelectedCourseId(coursesData[0].id);
        loadModulesForCourse(coursesData[0].id);
      }
      
      setError('');
    } catch (error) {
      console.error('Failed to load course data:', error);
      setError('Failed to load course data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadModulesForCourse = async (courseId) => {
    try {
      const modulesData = await apiService.getCourseModules(courseId);
      setModules(Array.isArray(modulesData) ? modulesData : []);
    } catch (error) {
      console.error('Failed to load modules:', error);
      setModules([]);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      setCreatingCourse(true);
      setError('');
      setSuccess('');
      const response = await apiService.createCourse(newCourse);
      setNewCourse({
        name: '',
        description: '',
        write_up: '',
        duration_months: '',
        cost_amount: '',
        cost_currency: 'NGN',
        is_active: true,
        pdf_file: null
      });
      setShowCreateCourse(false);
      setSuccess(`✅ Course "${response.name || newCourse.name}" created successfully!`);
      // Reload courses in background without blocking UI
      loadCourseData().catch(err => console.error('Failed to reload courses:', err));
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Failed to create course:', error);
      setError('Failed to create course. Please try again.');
      setSuccess('');
    } finally {
      setCreatingCourse(false);
    }
  };

  const handleAddModule = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await apiService.createModule({
        ...newModule,
        course: selectedCourseId || courses[0]?.id
      });
      if (selectedCourseId) {
        await loadModulesForCourse(selectedCourseId);
      }
      setNewModule({
        name: '',
        description: '',
        course: '',
        duration: '',
        learning_objectives: ''
      });
      setShowAddModule(false);
      setError('');
    } catch (error) {
      console.error('Failed to add module:', error);
      setError('Failed to add module. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse duration and convert to comparable number (in weeks)
  const parseDurationToWeeks = (duration) => {
    if (!duration || duration === 'TBD') return 0;
    
    const durationStr = duration.toLowerCase();
    const numberMatch = durationStr.match(/(\d+(?:\.\d+)?)/);
    
    if (!numberMatch) return 0;
    
    const number = parseFloat(numberMatch[1]);
    
    if (durationStr.includes('month')) {
      return number * 4.33; // Convert months to weeks (average 4.33 weeks per month)
    } else if (durationStr.includes('week')) {
      return number;
    } else if (durationStr.includes('year')) {
      return number * 52; // Convert years to weeks
    }
    
    // If no unit specified, assume months for admin console
    return number * 4.33;
  };

  // Helper function to get color gradients
  const getColorGradient = (index) => {
    const gradients = [
      'from-blue-500 via-indigo-500 to-purple-600',
      'from-green-500 via-emerald-500 to-teal-600',
      'from-red-500 via-pink-500 to-rose-600',
      'from-purple-500 via-violet-500 to-indigo-600',
      'from-yellow-500 via-orange-500 to-red-600',
      'from-cyan-500 via-blue-500 to-indigo-600'
    ];
    return gradients[index % gradients.length];
  };

  // Helper function to filter courses based on search query
  const filterCourses = (courses, query) => {
    if (!query.trim()) return courses;
    
    const searchTerm = query.toLowerCase().trim();
    
    return courses.filter(course => {
      const searchableFields = [
        course.title,
        course.description,
        course.teacher,
        course.status,
        course.level,
        course.duration
      ].map(field => (field || '').toLowerCase());
      
      return searchableFields.some(field => field.includes(searchTerm));
    });
  };

  // Process courses data to ensure consistent structure
  const processedCourses = filterCourses(
    courses.map((course, index) => ({
      ...course,
      title: course.name,
      modules: course.modules_count || 0,
      students: course.students_count || 0,
      materials: course.materials_count || 0,
      teacher: course.teacher?.full_name || 'Not Assigned',
      status: course.is_active ? 'Active' : 'Draft',
      level: course.level || 'Beginner to Advanced',
      duration: course.duration_months ? `${course.duration_months} months` : 'TBD',
      color: getColorGradient(index)
    })).sort((a, b) => {
      // Sort by duration in descending order (longest first)
      const durationA = parseDurationToWeeks(a.duration);
      const durationB = parseDurationToWeeks(b.duration);
      return durationB - durationA;
    }),
    searchQuery
  );

  // Process materials data
  const processedMaterials = materials.map((material) => ({
    ...material,
    name: material.title || material.name || 'Untitled Material',
    type: material.type || 'document',
    size: material.file_size || 'Unknown size',
    uploadDate: material.created_at ? new Date(material.created_at).toLocaleDateString() : 'Unknown date'
  }));

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/60';
      case 'Draft':
        return 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border border-orange-200/60';
      case 'Archived':
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200/60';
      default:
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200/60';
    }
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'pdf':
        return <DocumentIcon className="h-5 w-5 text-red-500" />;
      case 'video':
        return <PlayIcon className="h-5 w-5 text-blue-500" />;
      case 'document':
        return <DocumentTextIcon className="h-5 w-5 text-green-500" />;
      default:
        return <FolderIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">Course Management</h1>
          <p className="mt-2 text-gray-600 font-medium">
            Create and manage courses, modules, and learning materials
          </p>
        </div>
        <button 
          onClick={() => {
            setError('');
            setSuccess('');
            setShowCreateCourse(true);
          }}
          className="mt-6 sm:mt-0 btn-primary group"
        >
          <PlusIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
          Create Course
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800 font-medium">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800 font-medium">{error}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg group-hover:shadow-xl group-hover:shadow-blue-500/25 transition-all duration-300">
              <BookOpenIcon className="h-6 w-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Total Courses</p>
              <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {loading ? '...' : processedCourses.length}
              </p>
            </div>
          </div>
        </div>
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-4 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg group-hover:shadow-xl group-hover:shadow-green-500/25 transition-all duration-300">
              <AcademicCapIcon className="h-6 w-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Active Courses</p>
              <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {loading ? '...' : processedCourses.filter(c => c.status === 'Active').length}
              </p>
            </div>
          </div>
        </div>
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-500 to-violet-600 shadow-lg group-hover:shadow-xl group-hover:shadow-purple-500/25 transition-all duration-300">
              <DocumentTextIcon className="h-6 w-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Total Modules</p>
              <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {loading ? '...' : processedCourses.reduce((sum, course) => sum + course.modules, 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-4 rounded-2xl bg-gradient-to-r from-orange-500 to-red-600 shadow-lg group-hover:shadow-xl group-hover:shadow-orange-500/25 transition-all duration-300">
              <FolderIcon className="h-6 w-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Materials</p>
              <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {loading ? '...' : processedCourses.reduce((sum, course) => sum + course.materials, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search courses by name, teacher, status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-200/60 rounded-2xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-500 text-sm font-medium transition-all duration-200"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        <div className="text-sm text-gray-600 font-medium">
          {searchQuery ? (
            <>
              {processedCourses.length} course{processedCourses.length !== 1 ? 's' : ''} found
              {processedCourses.length === 0 && (
                <span className="text-orange-600 ml-2">Try a different search term</span>
              )}
            </>
          ) : (
            `${processedCourses.length} total course${processedCourses.length !== 1 ? 's' : ''}`
          )}
        </div>
      </div>

      {/* Courses Table - Tabular View */}
      <div className="bg-white/90 backdrop-blur-xl border border-white/30 rounded-2xl shadow-xl shadow-black/5 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <h3 className="text-lg font-bold text-gray-900">All Courses</h3>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading courses...</div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="text-red-500">{error}</div>
          </div>
        ) : processedCourses.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500">No courses created yet. Create your first course to get started!</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/80 backdrop-blur-sm">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Course</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Modules</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Students</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Materials</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/50">
                {processedCourses.map((course, index) => (
                  <tr 
                    key={course.id} 
                    className={`hover:bg-gray-50/60 transition-all duration-200 ${isLoaded ? 'animate-in fade-in slide-in-from-bottom-1' : 'opacity-0'}`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {/* Course Info Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${course.color} mr-3 shadow-lg flex-shrink-0`}></div>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-gray-900 truncate max-w-xs">
                            {course.title}
                          </div>
                          <div className="text-xs text-gray-600 truncate max-w-xs">
                            {course.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Status Column */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${getStatusColor(course.status)}`}>
                        {course.status}
                      </span>
                    </td>
                    
                    {/* Modules Column */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">{course.modules}</div>
                      <div className="text-xs text-gray-500">{course.modules === 1 ? 'module' : 'modules'}</div>
                    </td>
                    
                    {/* Students Column */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">{course.students}</div>
                      <div className="text-xs text-gray-500">enrolled</div>
                    </td>
                    
                    {/* Duration Column */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{course.duration}</div>
                    </td>
                    
                    {/* Materials Column */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">{course.materials}</div>
                      <div className="text-xs text-gray-500">files</div>
                    </td>
                    
                    {/* Actions Column */}
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => setShowAddModule(true)}
                          className="inline-flex items-center px-2 py-1 bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300 font-medium text-xs rounded-md transition-all duration-200 group"
                          title="Add Module"
                        >
                          <PlusIcon className="h-3 w-3 mr-1 group-hover:scale-110 transition-transform duration-200" />
                          Module
                        </button>
                        <button 
                          className="inline-flex items-center px-2 py-1 bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100 hover:border-purple-300 font-medium text-xs rounded-md transition-all duration-200 group"
                          title="Upload Files"
                        >
                          <CloudArrowUpIcon className="h-3 w-3 mr-1 group-hover:scale-110 transition-transform duration-200" />
                          Files
                        </button>
                        <button 
                          className="inline-flex items-center px-2 py-1 bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300 font-medium text-xs rounded-md transition-all duration-200 group"
                          title="Assign Teacher"
                        >
                          <AcademicCapIcon className="h-3 w-3 mr-1 group-hover:scale-110 transition-transform duration-200" />
                          Teacher
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Course Details Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Modules */}
        <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-xl shadow-black/5">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">Course Modules</h3>
            <button 
              onClick={() => setShowAddModule(true)}
              className="bg-white/80 backdrop-blur-sm border border-gray-200/60 text-gray-700 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/80 font-semibold py-2 px-4 rounded-2xl transition-all duration-300 group flex items-center text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
              Add Module
            </button>
          </div>
          
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-4 text-gray-500">Loading modules...</div>
            ) : modules.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No modules available. Add modules to get started!</div>
            ) : (
              modules.map((module) => (
              <div key={module.id} className="flex items-center justify-between p-4 bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/40 hover:bg-white/80 transition-all duration-300 group">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-4 ${
                    module.completed ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gray-300'
                  }`}></div>
                                  <div>
                  <p className="font-bold text-gray-900">{module.name || module.title}</p>
                  <p className="text-sm text-gray-600">{module.duration || 'Duration TBD'} • {module.materials_count || 0} materials</p>
                </div>
              </div>
              <button className="text-gray-400 hover:text-indigo-600 transition-colors duration-200">
                <DocumentTextIcon className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
              </button>
            </div>
              ))
            )}
        </div>
        </div>

        {/* Materials */}
        <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-xl shadow-black/5">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">Course Materials</h3>
            <button className="bg-white/80 backdrop-blur-sm border border-gray-200/60 text-gray-700 hover:text-purple-600 hover:border-purple-300 hover:bg-purple-50/80 font-semibold py-2 px-4 rounded-2xl transition-all duration-300 group flex items-center text-sm">
              <CloudArrowUpIcon className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
              Upload
            </button>
          </div>
          
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-4 text-gray-500">Loading materials...</div>
            ) : processedMaterials.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No materials available. Upload materials to get started!</div>
            ) : (
              processedMaterials.map((material) => (
              <div key={material.id} className="flex items-center justify-between p-4 bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/40 hover:bg-white/80 transition-all duration-300 group">
                <div className="flex items-center">
                  {getFileTypeIcon(material.type)}
                  <div className="ml-3">
                    <p className="font-bold text-gray-900">{material.name}</p>
                    <p className="text-sm text-gray-600">{material.size} • {material.uploadDate}</p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-green-600 transition-colors duration-200">
                  <CloudArrowUpIcon className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                </button>
              </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create Course Modal */}
      {showCreateCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Create New Course</h3>
              <button
                onClick={() => setShowCreateCourse(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleCreateCourse} className="space-y-4">
              <div>
                <label className="form-label mb-1">Course Title</label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="e.g., Advanced Machine Learning"
                  value={newCourse.name}
                  onChange={(e) => setNewCourse({...newCourse, name: e.target.value})}
                  required
                  disabled={creatingCourse}
                />
              </div>

              <div>
                <label className="form-label mb-1">Description</label>
                <textarea 
                  className="input-field" 
                  rows="3" 
                  placeholder="Course description..."
                  value={newCourse.description}
                  onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                  required
                  disabled={creatingCourse}
                />
              </div>

              <div>
                <label className="form-label mb-1">Course Write-up/Notes</label>
                <textarea 
                  className="input-field" 
                  rows="5" 
                  placeholder="Custom class write-up, notes, or additional information for this course..."
                  value={newCourse.write_up}
                  onChange={(e) => setNewCourse({...newCourse, write_up: e.target.value})}
                  disabled={creatingCourse}
                />
                <p className="text-xs text-gray-500 mt-1">This content will be displayed to students alongside the course materials</p>
              </div>

              <div>
                <label className="form-label mb-1">Course PDF Material</label>
                <input 
                  type="file" 
                  accept=".pdf"
                  className="input-field"
                  onChange={(e) => setNewCourse({...newCourse, pdf_file: e.target.files[0]})}
                  disabled={creatingCourse}
                />
                <p className="text-xs text-gray-500 mt-1">Upload a PDF document with course materials, notes, or resources</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label mb-1">Duration (months)</label>
                  <input 
                    type="number" 
                    className="input-field" 
                    placeholder="6"
                    value={newCourse.duration_months}
                    onChange={(e) => setNewCourse({...newCourse, duration_months: e.target.value})}
                    required
                    disabled={creatingCourse}
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={newCourse.is_active}
                      onChange={(e) => setNewCourse({...newCourse, is_active: e.target.checked})}
                      disabled={creatingCourse}
                    />
                    <span className="text-sm font-medium text-gray-700">Make Course Active</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label mb-1">Course Cost</label>
                  <input 
                    type="number" 
                    step="0.01"
                    className="input-field" 
                    placeholder="0.00"
                    value={newCourse.cost_amount}
                    onChange={(e) => setNewCourse({...newCourse, cost_amount: e.target.value})}
                    disabled={creatingCourse}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty if course is free</p>
                </div>
                <div>
                  <label className="form-label mb-1">Currency</label>
                  <select 
                    className="input-field"
                    value={newCourse.cost_currency}
                    onChange={(e) => setNewCourse({...newCourse, cost_currency: e.target.value})}
                    disabled={creatingCourse}
                  >
                    <option value="NGN">NGN (Nigerian Naira)</option>
                    <option value="USD">USD (US Dollar)</option>
                    <option value="GBP">GBP (Pound Sterling)</option>
                    <option value="EUR">EUR (Euro)</option>
                  </select>
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button 
                  type="submit" 
                  className="flex-1 btn-primary disabled:opacity-50"
                  disabled={creatingCourse}
                >
                  {creatingCourse ? 'Creating...' : 'Create Course'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateCourse(false)}
                  className="flex-1 btn-secondary"
                  disabled={creatingCourse}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Module Modal */}
      {showAddModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Add New Module</h3>
              <button
                onClick={() => setShowAddModule(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleAddModule} className="space-y-4">
              <div>
                <label className="form-label mb-1">Module Title</label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="e.g., Introduction to Python"
                  value={newModule.name}
                  onChange={(e) => setNewModule({...newModule, name: e.target.value})}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="form-label mb-1">Duration</label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="e.g., 2 weeks"
                  value={newModule.duration}
                  onChange={(e) => setNewModule({...newModule, duration: e.target.value})}
                  disabled={loading}
                />
              </div>

              <div>
                <label className="form-label mb-1">Description</label>
                <textarea 
                  className="input-field" 
                  rows="3" 
                  placeholder="Module description..."
                  value={newModule.description}
                  onChange={(e) => setNewModule({...newModule, description: e.target.value})}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="form-label mb-1">Learning Objectives</label>
                <textarea 
                  className="input-field" 
                  rows="3" 
                  placeholder="What will students learn?"
                  value={newModule.learning_objectives}
                  onChange={(e) => setNewModule({...newModule, learning_objectives: e.target.value})}
                  disabled={loading}
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button 
                  type="submit" 
                  className="flex-1 btn-primary disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Module'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModule(false)}
                  className="flex-1 btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseManagement; 